import { FC } from 'react'

type PageItem = number | 'ellipsis'

const range = (start: number, end: number) => {
  const pages: number[] = []
  for (let page = start; page <= end; page += 1) {
    pages.push(page)
  }
  return pages
}

const getPageItems = (page: number, totalPages: number): PageItem[] => {
  const siblingCount = 1
  const totalPageNumbers = siblingCount * 2 + 5

  if (totalPages <= totalPageNumbers) {
    return range(1, totalPages)
  }

  const leftSibling = Math.max(page - siblingCount, 1)
  const rightSibling = Math.min(page + siblingCount, totalPages)
  const showLeftEllipsis = leftSibling > 2
  const showRightEllipsis = rightSibling < totalPages - 1

  if (!showLeftEllipsis && showRightEllipsis) {
    const leftItemCount = 3 + siblingCount * 2
    return [...range(1, leftItemCount), 'ellipsis', totalPages]
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    const rightItemCount = 3 + siblingCount * 2
    return [
      1,
      'ellipsis',
      ...range(totalPages - rightItemCount + 1, totalPages),
    ]
  }

  return [
    1,
    'ellipsis',
    ...range(leftSibling, rightSibling),
    'ellipsis',
    totalPages,
  ]
}

export const DebugPagination: FC<{
  page: number
  totalPages: number
  total: number
  perPage?: number
  perPageOptions?: number[]
  isFetching?: boolean
  onPageChange: (page: number) => void
  onPerPageChange?: (perPage: number) => void
}> = ({
  page,
  totalPages,
  total,
  perPage,
  perPageOptions,
  isFetching = false,
  onPageChange,
  onPerPageChange,
}) => {
  const hasPrevious = page > 1
  const hasNext = page < totalPages
  const pageItems = getPageItems(page, Math.max(totalPages, 1))

  return (
    <div className="debug-pagination">
      <div className="debug-meta">
        Total: {total} | Page {page}/{totalPages}
        {perPage ? ` | ${perPage} par page` : ''}
        {isFetching ? ' | Actualisation...' : ''}
      </div>
      <nav className="debug-page-nav" aria-label="Pagination">
        <button
          className="debug-page-badge"
          type="button"
          aria-label="Page précédente"
          disabled={!hasPrevious}
          onClick={() => onPageChange(page - 1)}
        >
          ‹
        </button>
        {pageItems.map((item, index) =>
          item === 'ellipsis' ? (
            <span className="debug-page-ellipsis" key={`ellipsis-${index}`}>
              …
            </span>
          ) : (
            <button
              key={item}
              className={`debug-page-badge${item === page ? ' is-current' : ''}`}
              type="button"
              aria-label={`Page ${item}`}
              aria-current={item === page ? 'page' : undefined}
              onClick={() => onPageChange(item)}
            >
              {item}
            </button>
          )
        )}
        <button
          className="debug-page-badge"
          type="button"
          aria-label="Page suivante"
          disabled={!hasNext}
          onClick={() => onPageChange(page + 1)}
        >
          ›
        </button>
      </nav>
      {perPageOptions && perPage && onPerPageChange && (
        <div className="debug-pager-form">
          <label className="debug-meta" htmlFor="debugPerPageSelect">
            Par page
          </label>
          <select
            className="debug-input"
            id="debugPerPageSelect"
            value={perPage}
            onChange={(event) => onPerPageChange(Number(event.target.value))}
          >
            {perPageOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  )
}
